import React, { useContext, useEffect } from "react";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import SaveToFile from "../files/SaveToFile";
import {
  getNotes,
  makeNote,
  addNote,
  updateNote,
  removeNote,
} from "../layout/layoutModel";

/**
 * StickyNotesPanel — the "Sticky Notes" app (mirrors the Windows Notes List).
 *
 * Lists EVERY note the user has (open + closed). Closing a note (its ✕) only
 * hides it from the desktop — it stays here. From this list the user can:
 *   - create a new note (+ New note),
 *   - click a note to (re)open it on the desktop,
 *   - delete a note for good (trash) — the only destructive action.
 */
const firstLine = (text) => {
  const t = (text || "").trim();
  if (!t) return "";
  const nl = t.indexOf("\n");
  return nl === -1 ? t : t.slice(0, nl);
};

const StickyNotesPanel = ({ onClose }) => {
  const { layout, updateLayout } = useContext(WebtopLayoutContext);
  const notes = getNotes(layout);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const createNote = () => {
    const id = `note-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    // Cascade new notes so they don't stack exactly on top of each other.
    const offset = (notes.length % 6) * 28;
    updateLayout({
      stickyNotes: addNote(layout, makeNote(id, 80 + offset, 80 + offset)).stickyNotes,
    });
  };

  // Click a note → make sure it's open on the desktop.
  const openNote = (id) =>
    updateLayout({ stickyNotes: updateNote(layout, id, { open: true }).stickyNotes });

  const deleteNote = (id) =>
    updateLayout({ stickyNotes: removeNote(layout, id).stickyNotes });

  return (
    <div className="webtop-personalize-backdrop" onMouseDown={onClose}>
      <div
        className="webtop-notes-app"
        role="dialog"
        aria-label="Sticky Notes"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="webtop-notes-app__head">
          <span className="webtop-notes-app__title">Sticky Notes</span>
          <button
            type="button"
            className="webtop-notes-app__new"
            onClick={createNote}
          >
            + New note
          </button>
          <button
            type="button"
            aria-label="Close"
            className="webtop-notes-app__close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="webtop-notes-app__list">
          {notes.length === 0 && (
            <div className="webtop-notes-app__empty">
              No notes yet. Create one with “+ New note”.
            </div>
          )}
          {notes.map((note) => {
            const closed = note.open === false;
            const preview = firstLine(note.text);
            return (
              <div
                key={note.id}
                className="webtop-notes-app__card"
                style={{ background: note.color }}
                role="button"
                tabIndex={0}
                title={closed ? "Open on desktop" : "Already on desktop"}
                onClick={() => openNote(note.id)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openNote(note.id)}
              >
                <div className="webtop-notes-app__card-text">
                  {preview || <span className="webtop-notes-app__card-empty">Empty note</span>}
                </div>
                <div className="webtop-notes-app__card-foot" onClick={(e) => e.stopPropagation()}>
                  <span className="webtop-notes-app__card-state">
                    {closed ? "Closed" : "On desktop"}
                  </span>
                  <div className="webtop-notes-app__card-actions">
                    <SaveToFile name={preview || "Sticky note"} content={note.text} label="" />
                    <button
                      type="button"
                      className="webtop-notes-app__del"
                      aria-label="Delete note"
                      title="Delete note"
                      onClick={() => deleteNote(note.id)}
                    >
                      🗑
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StickyNotesPanel;
